import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Row,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AuditOutlined,
  CalculatorOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { formatMoney, UAE_MONEY_PROFILE } from '@/utils/format';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';

const { Text, Paragraph } = Typography;

const AccrualManagementPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { indiaPack, defaultPayrollCurrency } = useHrmsPolicy();
  const money = (n: number | null | undefined) =>
    formatMoney(Number(n || 0), { ...UAE_MONEY_PROFILE, currency: defaultPayrollCurrency });
  const [period, setPeriod] = useState<Dayjs>(dayjs().startOf('month'));
  const [eosbAsOf, setEosbAsOf] = useState<Dayjs>(dayjs());
  const [preview, setPreview] = useState<any | null>(null);
  const [ledgerEmpId, setLedgerEmpId] = useState<string | null>(null);
  const [ledgerYear, setLedgerYear] = useState(dayjs().year());
  const [glPreview, setGlPreview] = useState<any | null>(null);
  const [reconAsOf, setReconAsOf] = useState<Dayjs>(dayjs());

  const year = period.year();
  const month = period.month() + 1;

  const leaveDashQ = useQuery({
    queryKey: [...hrmsKeys.all, 'leave-accrual-dashboard', year, month],
    queryFn: () => hrmsService.leaveAccrualDashboard({ year, month }),
  });

  const eosbDashQ = useQuery({
    queryKey: [...hrmsKeys.all, 'eosb-accrual-dashboard', eosbAsOf.format('YYYY-MM-DD')],
    queryFn: () => hrmsService.eosbAccrualDashboard({ as_of_date: eosbAsOf.format('YYYY-MM-DD') }),
  });

  const leaveBatchesQ = useQuery({
    queryKey: [...hrmsKeys.all, 'accrual-batches', 'LEAVE'],
    queryFn: () => hrmsService.listAccrualBatches({ kind: 'LEAVE', limit: 20 }),
  });

  const eosbBatchesQ = useQuery({
    queryKey: [...hrmsKeys.all, 'accrual-batches', 'EOSB'],
    queryFn: () => hrmsService.listAccrualBatches({ kind: 'EOSB', limit: 20 }),
  });

  const ledgerQ = useQuery({
    queryKey: [...hrmsKeys.all, 'leave-accrual-ledger', ledgerEmpId, ledgerYear],
    queryFn: () => hrmsService.employeeLeaveAccrualLedger(ledgerEmpId!, ledgerYear),
    enabled: !!ledgerEmpId,
  });

  const accountingConfigQ = useQuery({
    queryKey: [...hrmsKeys.all, 'accrual-accounting-config'],
    queryFn: () => hrmsService.accrualAccountingConfig(),
  });

  const reconRunsQ = useQuery({
    queryKey: [...hrmsKeys.all, 'accrual-recon-runs'],
    queryFn: () => hrmsService.listAccrualReconciliations({ limit: 15 }),
  });

  const closeGateQ = useQuery({
    queryKey: [...hrmsKeys.all, 'accrual-close-gate'],
    queryFn: () => hrmsService.accrualCloseGate(),
  });

  const previewMut = useMutation({
    mutationFn: () => hrmsService.previewLeaveAccrual({ year, month }),
    onSuccess: (data) => {
      setPreview(data);
      message.success(
        `Preview ready — ${data?.summary?.to_post ?? 0} line(s) to post, ${data?.summary?.already_posted ?? 0} already posted`
      );
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Preview failed'),
  });

  const runLeaveMut = useMutation({
    mutationFn: () => hrmsService.runLeaveAccrual({ year, month }),
    onSuccess: (data) => {
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      message.success(
        `Posted ${data?.accrued_rows ?? data?.count ?? 0} leave accrual row(s) for ${data?.period_label || ''}`
      );
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Accrual failed'),
  });

  const runEosbMut = useMutation({
    mutationFn: () =>
      hrmsService.runEosbAccrual({ as_of_date: eosbAsOf.format('YYYY-MM-DD') }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      message.success(
        `EOSB provision posted for ${data?.count ?? 0} employee(s). Closing liability ${money(data?.summary?.closing_liability)}`
      );
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'EOSB accrual failed'),
  });

  const valueBatchMut = useMutation({
    mutationFn: (batchId: string) => hrmsService.valueAccrualBatch(batchId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      message.success(
        `Valued ${data?.movement_count ?? 0} movement(s). Period expense ${money(data?.period_expense_total)} (${data?.valuation_method})`
      );
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Valuation failed'),
  });

  const glPreviewMut = useMutation({
    mutationFn: (batchId: string) => hrmsService.previewAccrualGl(batchId),
    onSuccess: (data) => {
      setGlPreview(data);
      if (data?.issues?.length) message.warning(data.issues[0]);
      else message.success('GL preview ready');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'GL preview failed'),
  });

  const glHandoffMut = useMutation({
    mutationFn: (batchId: string) => hrmsService.createAccrualGlHandoff(batchId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      setGlPreview(data);
      message.success(data?.message || `Draft JV ${data?.journal_voucher_number || ''} created`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'GL handoff failed'),
  });

  const reconMut = useMutation({
    mutationFn: () =>
      hrmsService.runAccrualReconciliation({
        benefit_type: 'ALL',
        as_of_date: reconAsOf.format('YYYY-MM-DD'),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      message.success(
        `Reconciliation ${data?.status}: variance ${money(data?.unexplained_variance)}`
      );
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Reconciliation failed'),
  });

  const signOffMut = useMutation({
    mutationFn: (runId: string) => hrmsService.signOffAccrualReconciliation(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      message.success('Reconciliation signed off');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Sign-off blocked'),
  });

  const complianceMut = useMutation({
    mutationFn: () => hrmsService.benefitComplianceScan(),
    onSuccess: (data) => {
      message[data?.ok ? 'success' : 'warning'](
        `Compliance ${data?.jurisdiction}: ${data?.exceptions?.length ?? 0} finding(s)`
      );
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Compliance scan failed'),
  });

  const leaveDash = leaveDashQ.data;
  const eosbDash = eosbDashQ.data;
  const accountingConfig = accountingConfigQ.data;

  const allBatches = useMemo(() => {
    const leave = leaveBatchesQ.data || [];
    const eosb = eosbBatchesQ.data || [];
    return [...leave, ...eosb].sort(
      (a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()
    );
  }, [leaveBatchesQ.data, eosbBatchesQ.data]);

  const previewColumns: ColumnsType<any> = useMemo(
    () => [
      { title: 'Employee', dataIndex: 'employee_code', width: 110 },
      { title: 'Name', dataIndex: 'full_name', ellipsis: true },
      { title: 'Leave type', dataIndex: 'leave_type', width: 100 },
      {
        title: 'Days',
        dataIndex: 'amount',
        width: 90,
        render: (v, r) => (
          <Space size={4}>
            <span>{Number(v).toFixed(4)}</span>
            {r.prorated ? <Tag color="blue">Prorated</Tag> : null}
            {r.already_posted ? <Tag>Posted</Tag> : null}
          </Space>
        ),
      },
      {
        title: 'Projected closing',
        dataIndex: 'projected_closing',
        width: 120,
        render: (v) => Number(v).toFixed(2),
      },
      {
        title: '',
        key: 'open',
        width: 90,
        render: (_, r) => (
          <Button type="link" size="small" onClick={() => setLedgerEmpId(r.employee_id)}>
            Ledger
          </Button>
        ),
      },
    ],
    []
  );

  const batchColumns: ColumnsType<any> = [
    { title: 'When', dataIndex: 'created_at', width: 170, render: (v) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—') },
    {
      title: 'Period',
      key: 'period',
      width: 110,
      render: (_, r) =>
        r.accrual_kind === 'EOSB'
          ? r.as_of_date
          : `${r.period_year}-${String(r.period_month || '').padStart(2, '0')}`,
    },
    { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Finance',
      dataIndex: 'finance_status',
      width: 120,
      render: (v) => <Tag color={v === 'PostedToGl' ? 'green' : v === 'DraftHandoff' ? 'blue' : undefined}>{v || 'Unposted'}</Tag>,
    },
    { title: 'Rows', dataIndex: 'row_count', width: 70 },
    { title: 'Skipped', dataIndex: 'skipped_count', width: 80 },
    { title: 'Policy', dataIndex: 'policy_pack', ellipsis: true },
  ];

  const submitAcctMut = useMutation({
    mutationFn: (batchId: string) => hrmsService.submitAccrualAccounting(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      message.success('Submitted for accounting approval');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const approveAcctMut = useMutation({
    mutationFn: (batchId: string) => hrmsService.approveAccrualAccounting(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrmsKeys.all });
      message.success('Accounting batch approved — freeze applied; Draft JV still required');
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Approve failed'),
  });

  const accountingBatchColumns: ColumnsType<any> = [
    ...batchColumns.slice(0, 4),
    {
      title: 'Lifecycle',
      dataIndex: 'accounting_lifecycle',
      width: 130,
      render: (v) => <Tag color={v === 'Approved' || v === 'Posted' ? 'green' : undefined}>{v || 'Unvalued'}</Tag>,
    },
    {
      title: 'Method',
      dataIndex: 'valuation_method',
      width: 120,
      render: (v) => v || '—',
    },
    {
      title: 'Entitlement',
      dataIndex: 'entitlement_total',
      width: 120,
      render: (v) => money(v),
    },
    {
      title: 'Acct liability',
      dataIndex: 'accounting_liability_total',
      width: 120,
      render: (v) => money(v),
    },
    {
      title: 'Period expense',
      dataIndex: 'period_expense_total',
      width: 120,
      render: (v) => money(v),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 360,
      fixed: 'right',
      render: (_, r) => (
        <Space size={4} wrap>
          <Button size="small" loading={valueBatchMut.isPending} onClick={() => valueBatchMut.mutate(r.id)}>
            Value
          </Button>
          <Button size="small" loading={glPreviewMut.isPending} onClick={() => glPreviewMut.mutate(r.id)}>
            Preview
          </Button>
          <Button size="small" loading={submitAcctMut.isPending} onClick={() => submitAcctMut.mutate(r.id)}>
            Submit
          </Button>
          <Button size="small" loading={approveAcctMut.isPending} onClick={() => approveAcctMut.mutate(r.id)}>
            Approve
          </Button>
          <Button
            size="small"
            type="primary"
            loading={glHandoffMut.isPending}
            disabled={r.accounting_lifecycle !== 'Approved' && r.accounting_lifecycle !== 'JvDrafted'}
            onClick={() =>
              modal.confirm({
                title: 'Create draft Finance JV?',
                content:
                  'Requires Approved lifecycle. Creates a Draft journal for period expense only. Finance posts — HRMS never posts GL. Retries are idempotent.',
                okText: 'Create draft JV',
                onOk: () => glHandoffMut.mutateAsync(r.id),
              })
            }
          >
            Draft JV
          </Button>
        </Space>
      ),
    },
  ];

  const confirmLeaveRun = () => {
    modal.confirm({
      title: `Process leave accrual for ${period.format('MMM YYYY')}?`,
      content:
        'Eligible employees will be accrued under the company HRMS policy pack. Already-posted lines for this period are skipped (idempotent).',
      okText: 'Process',
      onOk: () => runLeaveMut.mutateAsync(),
    });
  };

  const confirmEosbRun = () => {
    modal.confirm({
      title: `Process EOSB provision as of ${eosbAsOf.format('DD MMM YYYY')}?`,
      content:
        indiaPack
          ? 'Uses the company gratuity formula from HRMS Setup (India POGA 1972 when India pack is active). Estimated entitlement and accounting liability are stored per employee.'
          : 'Uses the company EOSB formula from HRMS Setup (UAE Federal Decree-Law 33/2021 by default). Estimated entitlement and accounting liability are stored per employee.',
      okText: 'Process',
      onOk: () => runEosbMut.mutateAsync(),
    });
  };

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Accrual Management"
        subtitle="Leave and EOSB accrual processing, accounting valuation, Finance JV handoff, and HRMS↔GL reconciliation"
        primaryActions={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: hrmsKeys.all })}>
              Refresh
            </Button>
            <Button>
              <Link to="/hr/setup">HRMS Setup policies</Link>
            </Button>
          </Space>
        }
      />

      <Tabs
        items={[
          {
            key: 'leave',
            label: 'Leave Accrual',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card size="small">
                  <Space wrap>
                    <Text strong>Accrual period</Text>
                    <DatePicker
                      picker="month"
                      value={period}
                      onChange={(v) => v && setPeriod(v.startOf('month'))}
                      allowClear={false}
                    />
                    <Button
                      icon={<CalculatorOutlined />}
                      loading={previewMut.isPending}
                      onClick={() => previewMut.mutate()}
                    >
                      Validate &amp; preview
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      loading={runLeaveMut.isPending}
                      onClick={confirmLeaveRun}
                    >
                      Process monthly accrual
                    </Button>
                  </Space>
                  <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    Policy pack drives entitlement. Mid-month joiners/leavers are prorated. Duplicate runs for the same
                    employee, leave type, and period are blocked.
                  </Paragraph>
                </Card>

                <Row gutter={[16, 16]}>
                  <Col xs={12} md={6}>
                    <Card loading={leaveDashQ.isLoading}>
                      <Statistic
                        title="Eligible employees"
                        value={leaveDash?.eligible_employees ?? 0}
                        prefix={<TeamOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={leaveDashQ.isLoading}>
                      <Statistic
                        title="Month accrued days"
                        value={Number(leaveDash?.month_accrued_days || 0)}
                        precision={2}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={leaveDashQ.isLoading}>
                      <Statistic
                        title="Closing leave days"
                        value={Number(leaveDash?.balances?.closing || 0)}
                        precision={2}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={leaveDashQ.isLoading}>
                      <Statistic
                        title="Outstanding liability"
                        value={money(leaveDash?.outstanding_liability_aed)}
                      />
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={10}>
                    <Card
                      title="Period status"
                      loading={leaveDashQ.isLoading}
                      extra={<Tag>{leaveDash?.processing_status || '—'}</Tag>}
                    >
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Policy pack">{leaveDash?.policy_pack || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Utilized (year)">
                          {Number(leaveDash?.balances?.consumed || 0).toFixed(2)} days
                        </Descriptions.Item>
                        <Descriptions.Item label="Carried forward">
                          {Number(leaveDash?.balances?.carried_forward || 0).toFixed(2)} days
                        </Descriptions.Item>
                        <Descriptions.Item label="Encashed">
                          {Number(leaveDash?.balances?.encashed || 0).toFixed(2)} days
                        </Descriptions.Item>
                        <Descriptions.Item label="Excluded this preview">
                          {leaveDash?.excluded?.length ?? leaveDash?.preview_summary?.skipped ?? 0}
                        </Descriptions.Item>
                      </Descriptions>
                      {(leaveDash?.excluded?.length ?? 0) > 0 && (
                        <Alert
                          style={{ marginTop: 12 }}
                          type="warning"
                          showIcon
                          message={`${leaveDash.excluded.length} exclusion(s)`}
                          description="Open Validate & preview for employee-level reasons (probation/join/leave timing, zero entitlement)."
                        />
                      )}
                    </Card>
                  </Col>
                  <Col xs={24} lg={14}>
                    <Card
                      title={
                        <Space>
                          <HistoryOutlined />
                          <span>Leave accrual batches</span>
                        </Space>
                      }
                      loading={leaveBatchesQ.isLoading}
                    >
                      <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        columns={batchColumns}
                        dataSource={leaveBatchesQ.data || []}
                        scroll={{ x: 700 }}
                      />
                    </Card>
                  </Col>
                </Row>

                {preview && (
                  <Card
                    title={`Preview ${preview.period_label}`}
                    extra={
                      <Space>
                        <Tag color="processing">To post: {preview.summary?.to_post ?? 0}</Tag>
                        <Tag>Already posted: {preview.summary?.already_posted ?? 0}</Tag>
                        <Tag>Days: {Number(preview.summary?.days_to_accrue || 0).toFixed(2)}</Tag>
                      </Space>
                    }
                  >
                    <Table
                      size="small"
                      rowKey={(r) => `${r.employee_id}-${r.leave_type}`}
                      columns={previewColumns}
                      dataSource={preview.lines || []}
                      pagination={{ pageSize: 20 }}
                      scroll={{ x: 900 }}
                    />
                    {(preview.skipped?.length ?? 0) > 0 && (
                      <Alert
                        style={{ marginTop: 12 }}
                        type="info"
                        showIcon
                        message="Excluded employees / lines"
                        description={
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {(preview.skipped as any[]).slice(0, 15).map((s, i) => (
                              <li key={`${s.employee_id}-${i}`}>
                                {s.employee_code || s.employee_id} — {s.reason}
                                {s.leave_type ? ` (${s.leave_type})` : ''}
                              </li>
                            ))}
                          </ul>
                        }
                      />
                    )}
                  </Card>
                )}
              </Space>
            ),
          },
          {
            key: 'eosb',
            label: 'EOSB Accrual',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card size="small">
                  <Space wrap>
                    <Text strong>As-of date</Text>
                    <DatePicker value={eosbAsOf} onChange={(v) => v && setEosbAsOf(v)} allowClear={false} />
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      loading={runEosbMut.isPending}
                      onClick={confirmEosbRun}
                    >
                      Process EOSB provision
                    </Button>
                  </Space>
                  <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    Calculates statutory estimated entitlement via the HRMS policy pack formula and stores the
                    accounting liability snapshot. Final settlement remains on{' '}
                    <Link to="/hr/eosb">EOSB settlements</Link>. Automatic check-out / settlement is separate.
                  </Paragraph>
                </Card>

                {eosbDash?.disclaimer && (
                  <Alert type="info" showIcon message={eosbDash.disclaimer} />
                )}

                <Row gutter={[16, 16]}>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic title="Provisioned employees" value={eosbDash?.covered_employees ?? 0} />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic
                        title="Statutory entitlement (est.)"
                        value={money(
                          eosbDash?.estimated_statutory_entitlement ?? eosbDash?.total_liability
                        )}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic
                        title="Accounting liability"
                        value={money(eosbDash?.accounting_liability ?? eosbDash?.total_liability)}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic
                        title="Provisions this month"
                        value={eosbDash?.provisions_this_month ?? 0}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic title="Open settlements" value={eosbDash?.settlements_open ?? 0} />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic
                        title="Settlements paid YTD"
                        value={money(eosbDash?.settlements_paid_ytd_amount)}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic
                        title="Unposted accounting batches"
                        value={eosbDash?.unposted_accounting_batches ?? 0}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading} title="12m forecast (estimate)" size="small">
                      <Text strong>
                        {money(eosbDash?.forecast_12m?.estimated_entitlement_total)}
                      </Text>
                      <div>
                        <Text type="secondary">Estimates only — no journals</Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading} title="Policy / method" size="small">
                      <Text strong>{eosbDash?.policy_pack || '—'}</Text>
                      <div>
                        <Text type="secondary">{eosbDash?.valuation_method || 'STATUTORY_UAE'}</Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic title="Missing join date" value={eosbDash?.missing_join_date ?? 0} />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic
                        title="Alt. scheme skipped"
                        value={eosbDash?.alternative_scheme_skipped ?? 0}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card loading={eosbDashQ.isLoading}>
                      <Statistic
                        title="Not yet provisioned"
                        value={eosbDash?.not_yet_provisioned ?? 0}
                      />
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <Card title="Service milestones" loading={eosbDashQ.isLoading}>
                      <Table
                        size="small"
                        rowKey={(r: any) => String(r.employee_id)}
                        pagination={false}
                        dataSource={eosbDash?.milestones || []}
                        locale={{ emptyText: 'No employees near configured milestones' }}
                        columns={[
                          { title: 'Code', dataIndex: 'employee_code', width: 100 },
                          { title: 'Name', dataIndex: 'full_name', ellipsis: true },
                          {
                            title: 'Years',
                            dataIndex: 'service_years',
                            width: 80,
                            render: (v) => Number(v).toFixed(2),
                          },
                          { title: 'Note', dataIndex: 'milestone', ellipsis: true },
                        ]}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card
                      title={
                        <Space>
                          <AuditOutlined />
                          <span>EOSB provision batches</span>
                        </Space>
                      }
                      loading={eosbBatchesQ.isLoading}
                    >
                      <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        columns={batchColumns}
                        dataSource={eosbBatchesQ.data || []}
                        scroll={{ x: 700 }}
                      />
                    </Card>
                  </Col>
                </Row>

                <Card title="Latest employee provisions" loading={eosbDashQ.isLoading}>
                  <Table
                    size="small"
                    rowKey={(r: any) => String(r.employee_id)}
                    dataSource={eosbDash?.latest_provisions || []}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 900 }}
                    columns={[
                      {
                        title: 'Code',
                        dataIndex: 'employee_code',
                        width: 100,
                        render: (v, r: any) => (
                          <Link to={`/hr/eosb/profile/${r.employee_id}`}>{v || '—'}</Link>
                        ),
                      },
                      { title: 'Name', dataIndex: 'full_name', ellipsis: true },
                      {
                        title: 'Service yrs',
                        dataIndex: 'service_years',
                        width: 100,
                        render: (v) => Number(v).toFixed(2),
                      },
                      {
                        title: 'Basic',
                        dataIndex: 'basic_salary',
                        width: 120,
                        render: (v) => money(v),
                      },
                      {
                        title: 'Statutory / liability (mirror)',
                        dataIndex: 'provision_amount',
                        width: 160,
                        render: (v) => money(v),
                      },
                      { title: 'As of', dataIndex: 'as_of_date', width: 120 },
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: 'accounting',
            label: 'Accounting',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message="Entitlement · Accounting liability · Period expense are separate columns"
                  description={
                    accountingConfig?.note ||
                    'STATUTORY_UAE mirrors entitlement into liability by policy — not an IAS 19 / PUC claim. Map GL accounts on HRMS Setup, then Value → Preview JV → Draft JV. Finance posts.'
                  }
                />
                {(accountingConfig?.issues?.length ?? 0) > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Accounting configuration incomplete"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {accountingConfig.issues.map((i: string) => (
                          <li key={i}>{i}</li>
                        ))}
                      </ul>
                    }
                    action={
                      <Button size="small">
                        <Link to="/hr/setup">Open Setup</Link>
                      </Button>
                    }
                  />
                )}
                <Card
                  title="Accrual batches — valuation &amp; GL handoff"
                  extra={
                    <Text type="secondary">
                      Method: leave {accountingConfig?.['hrms.accounting.leave_valuation_method'] || '—'} · EOSB{' '}
                      {accountingConfig?.['hrms.accounting.eosb_valuation_method'] || '—'}
                    </Text>
                  }
                >
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={allBatches}
                    columns={accountingBatchColumns}
                    pagination={{ pageSize: 12 }}
                    scroll={{ x: 1200 }}
                    loading={leaveBatchesQ.isLoading || eosbBatchesQ.isLoading}
                  />
                </Card>
                {glPreview && (
                  <Card
                    title={`GL preview — ${glPreview.benefit_type} ${glPreview.period_label}`}
                    extra={
                      <Space>
                        <Tag>{glPreview.finance_status}</Tag>
                        {glPreview.journal_voucher_number ? (
                          <Tag color="blue">JV {glPreview.journal_voucher_number}</Tag>
                        ) : null}
                      </Space>
                    }
                  >
                    <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
                      <Descriptions.Item label="Entitlement">{money(glPreview.entitlement_total)}</Descriptions.Item>
                      <Descriptions.Item label="Accounting liability">
                        {money(glPreview.accounting_liability_total)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Period expense">{money(glPreview.period_expense_total)}</Descriptions.Item>
                      <Descriptions.Item label="Valuation">{glPreview.valuation_method}</Descriptions.Item>
                    </Descriptions>
                    {(glPreview.issues?.length ?? 0) > 0 && (
                      <Alert
                        style={{ marginBottom: 12 }}
                        type="error"
                        showIcon
                        message="Cannot hand off until resolved"
                        description={glPreview.issues.join(' ')}
                      />
                    )}
                    <Table
                      size="small"
                      rowKey={(_, i) => String(i)}
                      pagination={false}
                      dataSource={glPreview.lines || []}
                      columns={[
                        { title: 'Role', dataIndex: 'account_role', width: 140 },
                        {
                          title: 'Account',
                          key: 'acct',
                          render: (_: unknown, r: any) =>
                            r.account_code
                              ? `${r.account_code} — ${r.account_name || ''}`
                              : r.account_id || '— unmapped —',
                        },
                        { title: 'Debit', dataIndex: 'debit_amount', width: 110, render: (v) => money(v) },
                        { title: 'Credit', dataIndex: 'credit_amount', width: 110, render: (v) => money(v) },
                        { title: 'Description', dataIndex: 'description', ellipsis: true },
                      ]}
                    />
                  </Card>
                )}
              </Space>
            ),
          },
          {
            key: 'reconciliation',
            label: 'Reconciliation',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card size="small">
                  <Space wrap>
                    <Text strong>As of</Text>
                    <DatePicker value={reconAsOf} onChange={(v) => v && setReconAsOf(v)} allowClear={false} />
                    <Button type="primary" loading={reconMut.isPending} onClick={() => reconMut.mutate()}>
                      Run HRMS ↔ GL reconciliation
                    </Button>
                    <Button loading={complianceMut.isPending} onClick={() => complianceMut.mutate()}>
                      UAE compliance scan
                    </Button>
                  </Space>
                  <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    Unexplained variance blocks recon sign-off. Whether it blocks company financial close is a Finance
                    policy (`hrms.accounting.recon_blocks_close` sets the gate flag only).
                  </Paragraph>
                </Card>

                <Row gutter={[16, 16]}>
                  <Col xs={12} md={6}>
                    <Card loading={closeGateQ.isLoading}>
                      <Statistic
                        title="Close gate"
                        value={closeGateQ.data?.blocks_close ? 'Blocked' : 'Clear'}
                        valueStyle={{ color: closeGateQ.data?.blocks_close ? '#cf1322' : '#3f8600' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card>
                      <Statistic
                        title="Latest variance"
                        value={money(reconRunsQ.data?.[0]?.unexplained_variance)}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card>
                      <Statistic
                        title="HRMS liability"
                        value={money(reconRunsQ.data?.[0]?.hrms_accounting_liability_total)}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card>
                      <Statistic title="GL liability" value={money(reconRunsQ.data?.[0]?.gl_liability_total)} />
                    </Card>
                  </Col>
                </Row>

                {(complianceMut.data?.exceptions?.length ?? 0) > 0 && (
                  <Card title={`Compliance — ${complianceMut.data.jurisdiction}`}>
                    <Table
                      size="small"
                      rowKey={(_, i) => String(i)}
                      dataSource={complianceMut.data.exceptions}
                      pagination={{ pageSize: 10 }}
                      columns={[
                        {
                          title: 'Severity',
                          dataIndex: 'severity',
                          width: 100,
                          render: (v) => (
                            <Tag color={v === 'error' ? 'red' : v === 'warning' ? 'orange' : 'blue'}>{v}</Tag>
                          ),
                        },
                        { title: 'Rule', dataIndex: 'rule_code', width: 180 },
                        { title: 'Area', dataIndex: 'area', width: 80 },
                        { title: 'Message', dataIndex: 'message', ellipsis: true },
                      ]}
                    />
                  </Card>
                )}

                <Card title="Reconciliation runs" loading={reconRunsQ.isLoading}>
                  <Table
                    size="small"
                    rowKey="id"
                    dataSource={reconRunsQ.data || []}
                    pagination={false}
                    scroll={{ x: 1000 }}
                    columns={[
                      {
                        title: 'When',
                        dataIndex: 'created_at',
                        width: 160,
                        render: (v) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '—'),
                      },
                      { title: 'As of', dataIndex: 'as_of_date', width: 110 },
                      { title: 'Type', dataIndex: 'benefit_type', width: 80 },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        width: 100,
                        render: (v) => <Tag>{v}</Tag>,
                      },
                      {
                        title: 'HRMS liab.',
                        dataIndex: 'hrms_accounting_liability_total',
                        width: 120,
                        render: (v) => money(v),
                      },
                      {
                        title: 'GL liab.',
                        dataIndex: 'gl_liability_total',
                        width: 120,
                        render: (v) => money(v),
                      },
                      {
                        title: 'Variance',
                        dataIndex: 'unexplained_variance',
                        width: 110,
                        render: (v) => money(v),
                      },
                      {
                        title: 'Unposted',
                        dataIndex: 'unposted_approved_total',
                        width: 110,
                        render: (v) => money(v),
                      },
                      {
                        title: '',
                        key: 'sign',
                        width: 180,
                        render: (_, r) => (
                          <Space size={4}>
                            <Button
                              size="small"
                              onClick={async () => {
                                try {
                                  const d = await hrmsService.accrualReconciliationDrilldown(r.id);
                                  modal.info({
                                    title: 'Reconciliation drill-down',
                                    width: 720,
                                    content: (
                                      <div>
                                        <p>
                                          Employees: {d?.employees?.length ?? 0} · Pending batches:{' '}
                                          {d?.pending_batches?.length ?? 0} · GL lines: {d?.gl_lines?.length ?? 0} ·
                                          Settlements: {d?.settlements?.length ?? 0}
                                        </p>
                                        <ul style={{ paddingLeft: 18, maxHeight: 280, overflow: 'auto' }}>
                                          {(d?.exceptions || []).slice(0, 20).map((ex: any, i: number) => (
                                            <li key={i}>
                                              [{ex.root_cause || ex.code}] {ex.message}
                                            </li>
                                          ))}
                                        </ul>
                                        <p style={{ marginBottom: 0 }}>
                                          No automatic balancing journals are generated for unexplained differences.
                                        </p>
                                      </div>
                                    ),
                                  });
                                } catch (e: any) {
                                  message.error(e?.response?.data?.message || e?.message || 'Drill-down failed');
                                }
                              }}
                            >
                              Drill-down
                            </Button>
                            {r.status !== 'SignedOff' ? (
                              <Button
                                size="small"
                                loading={signOffMut.isPending}
                                onClick={() => signOffMut.mutate(r.id)}
                              >
                                Sign off
                              </Button>
                            ) : (
                              <Tag color="green">Signed</Tag>
                            )}
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title="Employee leave accrual ledger"
        width={720}
        open={!!ledgerEmpId}
        onClose={() => setLedgerEmpId(null)}
        destroyOnClose
      >
        <Space style={{ marginBottom: 12 }}>
          <Text>Year</Text>
          <DatePicker
            picker="year"
            value={dayjs().year(ledgerYear)}
            onChange={(v) => v && setLedgerYear(v.year())}
            allowClear={false}
          />
        </Space>
        {ledgerQ.isLoading ? (
          <Card loading />
        ) : ledgerQ.data ? (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Descriptions size="small" bordered column={1}>
              <Descriptions.Item label="Employee">
                {ledgerQ.data.employee?.employee_code} — {ledgerQ.data.employee?.full_name}
              </Descriptions.Item>
              <Descriptions.Item label="Policy pack">{ledgerQ.data.policy_pack}</Descriptions.Item>
            </Descriptions>
            <Table
              size="small"
              rowKey="leave_type"
              pagination={false}
              dataSource={ledgerQ.data.balances || []}
              columns={[
                { title: 'Type', dataIndex: 'leave_type' },
                { title: 'Opening', dataIndex: 'opening_balance' },
                { title: 'Accrued', dataIndex: 'accrued' },
                { title: 'Used', dataIndex: 'consumed' },
                { title: 'Carry', dataIndex: 'carried_forward' },
                { title: 'Encashed', dataIndex: 'encashed' },
                { title: 'Closing', dataIndex: 'closing_balance' },
                {
                  title: 'Liability',
                  dataIndex: 'financial_liability',
                  render: (v) => money(v),
                },
              ]}
            />
            <Table
              size="small"
              rowKey="id"
              title={() => 'Movements'}
              dataSource={ledgerQ.data.movements || []}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'accrual_date',
                  width: 110,
                  render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
                },
                { title: 'Type', dataIndex: 'leave_type', width: 90 },
                { title: 'Amount', dataIndex: 'amount', width: 90 },
                { title: 'Source', dataIndex: 'source', width: 140 },
                { title: 'Notes', dataIndex: 'notes', ellipsis: true },
              ]}
            />
          </Space>
        ) : null}
      </Drawer>
    </WorkspaceLayout>
  );
};

export default AccrualManagementPage;
